@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.screens_shared

import androidx.compose.runtime.*
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.launch
import kotlinx.coroutines.await
import kotlinx.serialization.decodeFromString
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.jsonArray
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import org.example.project.dashboard.DashboardTheme
import org.example.project.dashboard.POPUP_ENGINE_FLOATING_CHILD_Z_INDEX
import org.example.project.manager.LumaticaApiManager
import org.example.project.manager.LumaticaApiSettings
import org.example.project.manager.NodeScope
import org.example.project.service.FrameworkUserSettingsService
import org.example.project.service.LocalScreenInternalTitleVisible
import org.example.project.service.LoginService
import org.example.project.service.NotepadService
import org.example.project.settings.UISettings
import org.example.project.settings.LumaticaCheckboxSymbol
import org.example.project.settings.lumaticaCheckboxGlyph
import org.example.project.utils.All
import org.jetbrains.compose.web.ExperimentalComposeWebApi
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.events.Event
import org.w3c.dom.events.MouseEvent
import org.w3c.fetch.RequestInit

private const val NOTE_DARK_BACKGROUND = "linear-gradient(145deg, rgba(34,39,50,0.98), rgba(17,20,27,0.98))"
private val PRESET_BACKGROUNDS = listOf(NOTE_DARK_BACKGROUND)

private const val NOTE_BLOCKS_PREFIX = "__note_blocks_v1__:"

private const val NOTE_MIN_WIDTH = 260.0
private const val NOTE_MAX_WIDTH = 700.0
private const val NOTE_MIN_HEIGHT = 180.0
private const val NOTE_MAX_HEIGHT = 600.0

internal object NotepadStyles : StyleSheet() {
    val editor by style {
        property("overscroll-behavior", "contain")
        property("scroll-snap-type", "none")
        property("scrollbar-width", "thin")
        property("scrollbar-color", "rgba(56, 62, 74, 0.98) transparent")

        self + "::-webkit-scrollbar" style {
            width(12.px)
            height(12.px)
            backgroundColor(Color.transparent)
        }

        self + "::-webkit-scrollbar-track" style {
            backgroundColor(Color.transparent)
        }

        self + "::-webkit-scrollbar-thumb" style {
            borderRadius(999.px)
            property("background", "linear-gradient(180deg, rgba(72, 79, 92, 0.98) 0%, rgba(46, 52, 62, 0.98) 100%)")
            property("border", "1px solid rgba(255,255,255,0.10)")
        }

        self + "::-webkit-scrollbar-corner" style {
            backgroundColor(Color.transparent)
        }

        self + " img[data-note-drawing='true']" style {
            property("pointer-events", "none")
            property("user-select", "none")
            property("-webkit-user-drag", "none")
        }
    }

    val editorScrollable by style {
        overflowY("auto")
    }

    val editorLocked by style {
        overflowY("hidden")
    }
}

private val legacyNoteJson = Json {
    ignoreUnknownKeys = true
}

private val notizJson = Json {
    ignoreUnknownKeys = true
    encodeDefaults = false
}

private fun notizApiPart(value: String): String =
    js("encodeURIComponent")(if (value.isBlank()) "~" else value) as String

private fun currentUserId(): String =
    LoginService.getId()

private fun currentFirmenId(): String =
    LoginService.getUser()?.firmenId.orEmpty().trim()

private fun currentEmail(): String =
    LoginService.getUser()?.email.orEmpty().trim().lowercase()

private fun isNet(art: String): Boolean =
    art.uppercase() == NodeScope.NET

internal suspend fun loadNotesForArt(art: String): List<All> {
    val userId = currentUserId()
    if (userId.isBlank()) return emptyList()
    val raw = runCatching {
        if (isNet(art)) {
            val firmenId = currentFirmenId()
            val email = currentEmail()
            if (firmenId.isBlank() || email.isBlank()) "" else LumaticaApiManager.call("notepadsystem/company-user/${notizApiPart(firmenId)}/${notizApiPart(email)}")
        } else {
            LumaticaApiManager.call("notepadsystem/user/${notizApiPart(userId)}")
        }
    }.getOrDefault("")
    if (raw.isBlank()) return emptyList()
    return runCatching { notizJson.decodeFromString<List<All>>(raw) }.getOrDefault(emptyList())
}

internal suspend fun saveNoteForArt(art: String, note: All): All? {
    if (currentUserId().isBlank()) return null
    if (isNet(art) && (currentFirmenId().isBlank() || currentEmail().isBlank())) return null
    val noteToSave = if (note.id.equals("neu", ignoreCase = true)) note.copy(id = "") else note
    val endpoint = if (isNet(art)) "notepadsystem/company-save-json" else "notepadsystem/save-json"
    val body = if (isNet(art)) {
        """{"firmenId":${notizJson.encodeToString(currentFirmenId())},"email":${notizJson.encodeToString(currentEmail())},"note":${notizJson.encodeToString(All.AllNames.serializer(), noteToSave)}}"""
    } else {
        """{"userId":${notizJson.encodeToString(currentUserId())},"note":${notizJson.encodeToString(All.AllNames.serializer(), noteToSave)}}"""
    }
    val raw = runCatching {
        window.fetch(
            "${LumaticaApiSettings.getBaseUrl()}/api/$endpoint",
            RequestInit(
                method = "POST",
                headers = LumaticaApiManager.auditHeaders("application/json"),
                body = body
            )
        ).await().text().await()
    }.getOrDefault("")
    if (raw.isBlank() || raw == "null") return null
    return runCatching { notizJson.decodeFromString(All.AllNames.serializer(), raw) }.getOrNull()
}

internal suspend fun deleteNoteForArt(art: String, noteId: String) {
    val userId = currentUserId()
    if (userId.isBlank() || noteId.isBlank()) return
    if (isNet(art)) {
        val firmenId = currentFirmenId()
        val email = currentEmail()
        if (firmenId.isBlank() || email.isBlank()) return
        LumaticaApiManager.call("notepadsystem/company-delete/${notizApiPart(firmenId)}/${notizApiPart(email)}/${notizApiPart(noteId)}")
    } else {
        LumaticaApiManager.call("notepadsystem/delete/${notizApiPart(userId)}/${notizApiPart(noteId)}")
    }
}

internal fun normalizedNoteBackground(saved: String?): String {
    return NOTE_DARK_BACKGROUND
}

private fun escapeHtml(value: String): String = buildString {
    value.forEach { ch ->
        when (ch) {
            '&' -> append("&amp;")
            '<' -> append("&lt;")
            '>' -> append("&gt;")
            '"' -> append("&quot;")
            '\'' -> append("&#39;")
            else -> append(ch)
        }
    }
}

private fun textToEditableHtml(raw: String): String =
    if (raw.isBlank()) "" else escapeHtml(raw).replace("\n", "<br>")

private fun legacyBlocksToHtml(raw: String): String {
    val noteInhalt = raw.removePrefix(NOTE_BLOCKS_PREFIX)
    return try {
        val blocks = legacyNoteJson.parseToJsonElement(noteInhalt)
            .jsonObject["blocks"]
            ?.jsonArray
            .orEmpty()
            .map { element ->
                val obj = element.jsonObject
                All(
                    typ = obj["type"]?.jsonPrimitive?.content.orEmpty(),
                    bemerkung = obj["text"]?.jsonPrimitive?.content.orEmpty(),
                    aktiv = obj["checked"]?.jsonPrimitive?.content?.toBooleanStrictOrNull() ?: false
                )
            }

        blocks.joinToString("<br>") { block ->
            when (block.typ.uppercase()) {
                "STEP" -> stepTokenHtml() + "&nbsp;" + escapeHtml(block.bemerkung)
                "CHECKBOX" -> checkboxTokenHtml(block.aktiv) + "&nbsp;" + escapeHtml(block.bemerkung)
                else -> escapeHtml(block.bemerkung)
            }
        }
    } catch (_: Throwable) {
        textToEditableHtml(noteInhalt)
    }
}

internal fun normalizeStoredNoteHtml(raw: String?): String {
    val value = raw.orEmpty()
    if (value.isBlank()) return ""
    if (value.startsWith(NOTE_BLOCKS_PREFIX)) return legacyBlocksToHtml(value)
    if (Regex("<(?:span|img|br|div|p|ul|ol|li)\\b", RegexOption.IGNORE_CASE).containsMatchIn(value)) return normalizeCheckboxTokenHtml(value)
    return textToEditableHtml(value)
}

internal fun normalizeCheckboxTokenHtml(html: String): String {
    if (!html.contains("data-checkbox-state")) return html
    val host = document.createElement("div") as HTMLElement
    host.innerHTML = html
    val tokens = host.querySelectorAll("[data-checkbox-state]")
    for (index in 0 until tokens.length) {
        val token = tokens.item(index) as? HTMLElement ?: continue
        val checked = token.getAttribute("data-checkbox-state") == "true"
        token.textContent = lumaticaCheckboxGlyph(checked)
        token.setAttribute("style", "display:inline-flex;align-items:center;vertical-align:middle;cursor:pointer;font-size:18px;line-height:1;")
    }
    return host.innerHTML
}

internal fun taskParagraphHtml(html: String): String = html
    .replace(Regex("&lt;p\\s*&gt;", RegexOption.IGNORE_CASE), "<br>")
    .replace(Regex("&lt;/p\\s*&gt;", RegexOption.IGNORE_CASE), "")

internal fun taskParagraphLines(raw: String): List<String> {
    if (raw.isBlank()) return emptyList()
    val host = document.createElement("div") as HTMLElement
    host.innerHTML = taskParagraphHtml(raw)
    val tokens = host.querySelectorAll("[data-note-token]")
    for (index in 0 until tokens.length) {
        val token = tokens.item(index) as? HTMLElement ?: continue
        if (token.getAttribute("data-note-token") == "step") {
            token.replaceWith(document.createTextNode("- "))
        } else {
            token.remove()
        }
    }
    host.innerHTML = host.innerHTML
        .replace(Regex("<br\\s*/?>", RegexOption.IGNORE_CASE), "\n")
        .replace(Regex("</?(?:p|div|li)\\b[^>]*>", RegexOption.IGNORE_CASE), "\n")
    return host.textContent.orEmpty()
        .split(Regex("\\r?\\n"))
        .map { it.replace(Regex("\\s+"), " ").trim() }
        .filter { it.isNotEmpty() }
}

internal fun taskCompactPreview(raw: String): String = taskParagraphLines(raw).joinToString(" | ")

internal fun htmlToPreviewText(raw: String?): String {
    val value = normalizeStoredNoteHtml(raw)
    if (value.isBlank()) return "Leere Notiz"
    val host = document.createElement("div") as HTMLElement
    host.innerHTML = value
    val tokenNodes = host.querySelectorAll("[data-note-token]")
    for (i in 0 until tokenNodes.length) {
        val node = tokenNodes.item(i) as? HTMLElement ?: continue
        val label = when (node.getAttribute("data-note-token")) {
            "step" -> "Step"
            "checkbox-true" -> "Checkbox"
            "checkbox-false" -> "Checkbox"
            else -> ""
        }
        node.replaceWith(document.createTextNode(label))
    }
    val text = host.textContent.orEmpty()
        .replace('\n', ' ')
        .replace(Regex("\\s+"), " ")
        
    return text.ifBlank { "Leere Notiz" }
}

private fun footerButtonStyle(active: Boolean): StyleScope.() -> Unit = {
    width(34.px)
    height(34.px)
    borderRadius(10.px)
    border(
        1.px,
        LineStyle.Solid,
        if (active) UISettings.ACCENT else Color("rgba(255,255,255,0.16)")
    )
    backgroundColor(if (active) Color("rgba(255,255,255,0.12)") else Color("rgba(0,0,0,0.14)"))
    property("box-shadow", if (active) "0 0 18px ${UISettings.ACCENT}, 0 0 1px ${UISettings.ACCENT}" else "none")
    display(DisplayStyle.Flex)
    justifyContent(JustifyContent.Center)
    alignItems(AlignItems.Center)
    cursor("pointer")
    property("transition", "all 0.15s ease")
}

internal fun stepTokenHtml(): String =
    """<span data-note-token="step" contenteditable="false" style="display:inline-flex;align-items:center;vertical-align:middle;"><img src="images/step.svg" style="width:16px;height:16px;filter:brightness(0) invert(1);" /></span>"""

internal fun checkboxTokenHtml(checked: Boolean): String {
    val state = if (checked) "true" else "false"
    return """<span data-note-token="checkbox-$state" data-checkbox-state="$state" contenteditable="false" style="display:inline-flex;align-items:center;vertical-align:middle;cursor:pointer;font-size:18px;line-height:1;">${lumaticaCheckboxGlyph(checked)}</span>"""
}

internal fun toggleCheckboxToken(target: HTMLElement): Boolean {
    val token = generateSequence(target as HTMLElement?) { current ->
        current.parentElement as? HTMLElement
    }.firstOrNull { it.hasAttribute("data-checkbox-state") } ?: return false
    val checked = token.getAttribute("data-checkbox-state") == "true"
    val nextChecked = !checked
    token.setAttribute("data-checkbox-state", if (nextChecked) "true" else "false")
    token.setAttribute("data-note-token", if (nextChecked) "checkbox-true" else "checkbox-false")
    token.textContent = lumaticaCheckboxGlyph(nextChecked)
    token.setAttribute("style", "display:inline-flex;align-items:center;vertical-align:middle;cursor:pointer;font-size:18px;line-height:1;")
    return true
}

internal fun insertHtmlAtSelection(editor: HTMLElement?, html: String) {
    val target = editor ?: return
    target.focus()
    val selection = document.asDynamic().getSelection()
    if (selection == null || selection.rangeCount == 0) {
        target.insertAdjacentHTML("beforeend", html)
        return
    }
    val range = selection.getRangeAt(0)
    range.deleteContents()
    val wrapper = document.createElement("div")
    wrapper.innerHTML = html
    val fragment = document.createDocumentFragment()
    var lastNode: dynamic = null
    while (true) {
        val child = wrapper.firstChild ?: break
        lastNode = fragment.appendChild(child)
    }
    range.insertNode(fragment)
    if (lastNode != null) {
        range.setStartAfter(lastNode)
        range.collapse(true)
        selection.removeAllRanges()
        selection.addRange(range)
    }
}

internal fun placeEditorCursorAtEnd(editor: HTMLElement?): dynamic {
    val target = editor ?: return null
    target.focus()
    val selection = document.asDynamic().getSelection() ?: return null
    val range = document.asDynamic().createRange()
    range.selectNodeContents(target)
    range.collapse(false)
    selection.removeAllRanges()
    selection.addRange(range)
    return range.cloneRange()
}

private fun previousTokenSibling(node: dynamic): HTMLElement? {
    var current = node
    while (current != null) {
        val previous = current.previousSibling
        when {
            previous == null -> current = current.parentNode
            previous.nodeType == 3 -> {
                val text = (previous.textContent as? String).orEmpty()
                if (text.isNotEmpty()) return null
                current = previous
            }
            previous is HTMLElement && previous.hasAttribute("data-note-token") -> return previous
            else -> return null
        }
    }
    return null
}

private fun nextTokenSibling(node: dynamic): HTMLElement? {
    var current = node
    while (current != null) {
        val next = current.nextSibling
        when {
            next == null -> current = current.parentNode
            next.nodeType == 3 -> {
                val text = (next.textContent as? String).orEmpty()
                if (text.isNotEmpty()) return null
                current = next
            }
            next is HTMLElement && next.hasAttribute("data-note-token") -> return next
            else -> return null
        }
    }
    return null
}

private fun removeAdjacentToken(editor: HTMLElement?, backward: Boolean): Boolean {
    val target = editor ?: return false
    val selection = document.asDynamic().getSelection() ?: return false
    if (selection.rangeCount == 0) return false
    val range = selection.getRangeAt(0)
    if (!(range.collapsed as Boolean)) return false
    val container = range.startContainer
    val offset = (range.startOffset as? Int) ?: 0

    val token = when {
        container.nodeType == 3 -> {
            val text = (container.textContent as? String).orEmpty()
            if (backward && offset == 0) previousTokenSibling(container)
            else if (!backward && offset >= text.length) nextTokenSibling(container)
            else null
        }
        container is HTMLElement -> {
            if (backward && offset > 0) {
                val child = container.childNodes.item(offset - 1)
                if (child is HTMLElement && child.hasAttribute("data-note-token")) child else previousTokenSibling(child)
            } else if (!backward) {
                val child = container.childNodes.item(offset)
                if (child is HTMLElement && child.hasAttribute("data-note-token")) child else nextTokenSibling(child)
            } else null
        }
        else -> null
    } ?: return false

    val parent = token.parentNode ?: return false
    parent.removeChild(token)
    target.focus()
    return true
}

internal fun captureEditorSelection(editor: HTMLElement?): dynamic {
    val target = editor ?: return null
    val selection = document.asDynamic().getSelection() ?: return null
    if (selection.rangeCount == 0) return null
    val range = selection.getRangeAt(0)
    val anchorNode = selection.anchorNode
    if (anchorNode == null || !target.contains(anchorNode)) return null
    return range.cloneRange()
}

internal fun restoreEditorSelection(editor: HTMLElement?, savedRange: dynamic) {
    val target = editor ?: return
    val range = savedRange ?: return
    target.focus()
    val selection = document.asDynamic().getSelection() ?: return
    selection.removeAllRanges()
    selection.addRange(range)
}

@Composable
fun SHAREDNotitzen(
    art: String = org.example.project.service.currentSaveArt(),
    showLauncher: Boolean
) {
    Style(NotepadStyles)

    val scope = rememberCoroutineScope()
    val internalTitleVisible = LocalScreenInternalTitleVisible.current
    val notes    = NotepadService.getNotes(art)
    val openIds  = NotepadService.getOpenIds(art)
    val launcherNotes = remember(notes, openIds) {
        notes.filter { !it.art.equals("PAINT", ignoreCase = true) && it.id !in openIds }
    }

    LaunchedEffect(art) {
        NotepadService.setNotes(art, loadNotesForArt(art))
    }

    if (showLauncher) {
        Div({
            style {
                position(Position.Relative)
                width(100.percent)
                height(100.percent)
                overflow("visible")
            }
        }) {
            NotepadLauncher(
                notes = launcherNotes,
                onCreate = {
                    scope.launch {
                        val neu = All(
                            id = "neu",
                            titel = "Neue Notiz",
                            beschreibung = "",
                            farbe = PRESET_BACKGROUNDS[0],
                            art = "TEXT"
                        )
                        val saved = saveNoteForArt(art, neu) ?: return@launch
                        NotepadService.setNotes(art, NotepadService.getNotes(art) + saved)
                        NotepadService.openNote(art, saved.id)
                    }
                },
                onOpen   = { id -> NotepadService.openNote(art, id) },
                onDelete = { id ->
                    scope.launch {
                        deleteNoteForArt(art, id)
                        NotepadService.removeNote(art, id)
                    }
                }
            )
        }
    }
}

@Composable
fun NotepadOverlayLayer(art: String) {
    val scope  = rememberCoroutineScope()
    val notes  = NotepadService.getNotes(art)
    val openIds = NotepadService.getOpenIds(art)

    openIds.forEach { id ->
        val note = notes.firstOrNull { it.id == id && !it.art.equals("PAINT", ignoreCase = true) } ?: return@forEach
        DesktopPostit(
            note = note,
            onUpdate = { updated ->
                NotepadService.updateNote(art, updated)
                scope.launch {
                    saveNoteForArt(art, updated)?.let { saved ->
                        NotepadService.updateNote(art, saved)
                    }
                }
            },
            onClose  = { NotepadService.closeNote(art, id) },
            onDelete = {
                scope.launch {
                    deleteNoteForArt(art, note.id)
                    NotepadService.removeNote(art, id)
                }
            }
        )
    }
}

@OptIn(ExperimentalComposeWebApi::class)
@Composable
fun NotepadLauncher(
    notes: List<All>,
    onCreate: () -> Unit,
    onOpen: (String) -> Unit,
    onDelete: (String) -> Unit
) {
    val internalTitleVisible = LocalScreenInternalTitleVisible.current
    Div({
        style {
            width(100.percent)
            height(100.percent)
            backgroundColor(Color.transparent)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            overflow("hidden")
        }
    }) {
        if (internalTitleVisible) {
            Div({
                style {
                    padding(20.px, 25.px, 0.px, 25.px)
                    color(Color.white)
                    fontSize(28.px)
                    fontWeight("700")
                    textAlign("center")
                }
            }) { Text("Notitzen") }
        }
        Div({
            style {
                display(DisplayStyle.Grid)
                gridTemplateColumns("repeat(5, 150px)")
                gridAutoRows("110px")
                gap(20.px)
                padding(if (internalTitleVisible) 20.px else 12.px, 25.px, 25.px, 25.px)
                overflowY("auto")
                property("flex", "1")
                property("min-height", "0")
            }
        }) {
            BigPlusTile(onCreate)
            notes.forEach { note ->
                LauncherTile(note, { onOpen(note.id) }, { onDelete(note.id) })
            }
        }
    }
}

@Composable
private fun BigPlusTile(onCreate: () -> Unit) {
    Div({
        style {
            width(100.percent)
            height(100.percent)
            borderRadius(12.px)
            property("background", DashboardTheme.PREMIUM_SURFACE_BG)
            property("border", "2px dashed rgba(255,255,255,0.2)")
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            fontSize(46.px)
            color(Color.white)
            cursor("pointer")
        }
        onClick { onCreate() }
    }) { Text("+") }
}

@Composable
private fun LauncherTile(note: All, onOpen: (String) -> Unit, onDelete: (String) -> Unit) {
    Div({
        style {
            width(100.percent)
            height(100.percent)
            borderRadius(12.px)
            property("background", normalizedNoteBackground(note.farbe))
            property("box-sizing", "border-box")
            padding(10.px)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            justifyContent(JustifyContent.SpaceBetween)
            cursor("pointer")
        }
        onClick { onOpen(note.id) }
    }) {
        Div({ style { display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween) } }) {
            Span({
                style {
                    fontWeight("bold")
                    fontSize(12.px)
                    overflow("hidden")
                    color(Color.white)
                    property("text-overflow", "ellipsis")
                    property("white-space", "nowrap")
                }
            }) { Text(note.titel ?: "Notiz") }
            Span({
                style { cursor("pointer"); fontSize(16.px); fontWeight("bold"); color(Color.white) }
                onClick { it.stopPropagation(); onDelete(note.id) }
            }) { Text("×") }
        }
        Div({
            style {
                fontSize(11.px)
                color(Color.white)
                opacity(0.7)
                overflow("hidden")
                property("display", "-webkit-box")
                property("-webkit-line-clamp", "3")
                property("-webkit-box-orient", "vertical")
            }
        }) { Text(htmlToPreviewText(note.beschreibung)) }
    }
}

@Composable
fun DesktopPostit(note: All, onUpdate: (All) -> Unit, onClose: () -> Unit, onDelete: () -> Unit) {
    val isPaint = note.art.equals("PAINT", ignoreCase = true)
    val scope = rememberCoroutineScope()
    val noteKey = "note_${note.id}"
    val userSettingsVersion = FrameworkUserSettingsService.currentUserSettingsVersion()
    val saved = remember(note.id) { FrameworkUserSettingsService.getPopupSettings(noteKey) }

    var x by remember(note.id) { mutableStateOf(saved?.getOrNull(0) ?: 260.0) }
    var y by remember(note.id) { mutableStateOf(saved?.getOrNull(1) ?: 180.0) }
    var width by remember(note.id) { mutableStateOf(saved?.getOrNull(2) ?: 300.0) }
    var height by remember(note.id) { mutableStateOf(saved?.getOrNull(3) ?: 320.0) }

    LaunchedEffect(note.id, userSettingsVersion) {
        FrameworkUserSettingsService.ensureCurrentUserSettingsLoaded(forceReload = userSettingsVersion > 0L)
        val loaded = FrameworkUserSettingsService.getPopupSettings(noteKey)
        if (loaded != null) {
            x = loaded.getOrNull(0) ?: x
            y = loaded.getOrNull(1) ?: y
            width = loaded.getOrNull(2) ?: width
            height = loaded.getOrNull(3) ?: height
        }
    }
    var title by remember(note.id) { mutableStateOf(note.titel ?: "") }
    var editorHtml by remember(note.id, note.beschreibung) { mutableStateOf(normalizeStoredNoteHtml(note.beschreibung)) }
    var activeInsertMode by remember(note.id) { mutableStateOf<String?>(null) }
    var showDrawing by remember(note.id) { mutableStateOf(false) }
    var drawingImage by remember(note.id) { mutableStateOf<HTMLElement?>(null) }
    var noteColor by remember(note.id) { mutableStateOf("#ffffff") }
    var showColorPalette by remember(note.id) { mutableStateOf(false) }
    var drawingData by remember(note.id) { mutableStateOf(note.beschreibung.orEmpty().takeIf { it.startsWith("data:image/") }.orEmpty()) }
    var showMenu by remember(note.id) { mutableStateOf(false) }
    var dragging by remember(note.id) { mutableStateOf(false) }
    var resizing by remember(note.id) { mutableStateOf(false) }
    var startX by remember(note.id) { mutableStateOf(0.0) }
    var startY by remember(note.id) { mutableStateOf(0.0) }
    var startWidth by remember { mutableStateOf(300.0) }
    var startHeight by remember { mutableStateOf(320.0) }
    var editorElement by remember { mutableStateOf<HTMLElement?>(null) }
    var savedSelectionRange by remember(note.id) { mutableStateOf<dynamic>(null) }
    var hasEditorOverflow by remember(note.id) { mutableStateOf(false) }

    fun updateEditorOverflow() {
        val element = editorElement ?: run {
            hasEditorOverflow = false
            return
        }
        hasEditorOverflow = element.scrollHeight > element.clientHeight + 1
    }

    DisposableEffect(Unit) {
        val move: (Event) -> Unit = { ev ->
            val e = ev as MouseEvent
            if (dragging) {
                x += e.clientX.toDouble() - startX
                y += e.clientY.toDouble() - startY
                startX = e.clientX.toDouble()
                startY = e.clientY.toDouble()
            } else if (resizing) {
                val dx = e.clientX.toDouble() - startX
                val dy = e.clientY.toDouble() - startY
                width = (startWidth + dx).coerceIn(NOTE_MIN_WIDTH, NOTE_MAX_WIDTH)
                height = (startHeight + dy).coerceIn(NOTE_MIN_HEIGHT, NOTE_MAX_HEIGHT)
                window.setTimeout({ updateEditorOverflow() }, 0)
            }
        }
        val up: (Event) -> Unit = {
            if (dragging || resizing) {
                scope.launch {
                    FrameworkUserSettingsService.saveCurrentUserPopupSettings(noteKey, x, y, width, height)
                }
            }
            dragging = false
            resizing = false
        }
        document.addEventListener("mousemove", move)
        document.addEventListener("mouseup", up)
        onDispose {
            document.removeEventListener("mousemove", move)
            document.removeEventListener("mouseup", up)
        }
    }

    fun syncEditorFromDom() {
        editorHtml = editorElement?.innerHTML.orEmpty()
        updateEditorOverflow()
    }

    fun snapshotSelection() {
        savedSelectionRange = captureEditorSelection(editorElement)
    }

    fun persist() {
        if (isPaint) {
            val canvas = document.getElementById("desktop-note-drawing-${note.id}") as? HTMLCanvasElement
            if (canvas != null) drawingData = canvas.toDataURL("image/png")
        } else {
            syncEditorFromDom()
        }
        onUpdate(
            note.copy(
                titel = title,
                beschreibung = if (isPaint) drawingData else editorElement?.innerHTML.orEmpty(),
                art = if (isPaint) "PAINT" else "TEXT"
            )
        )
    }

    LaunchedEffect(height, editorHtml, showMenu) {
        window.setTimeout({ updateEditorOverflow() }, 0)
    }

    val editorScrollEnabled = height >= NOTE_MAX_HEIGHT && hasEditorOverflow

    Div({
        style {
            position(Position.Fixed)
            left(x.px)
            top(y.px)
            width(width.px)
            height(height.px)
            borderRadius(12.px)
            property("background", normalizedNoteBackground(note.farbe))
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            overflow("hidden")
            property("box-shadow", "0 12px 25px rgba(0,0,0,.5)")
            property("z-index", POPUP_ENGINE_FLOATING_CHILD_Z_INDEX.toString())
        }
    }) {
        Div({
            style {
                position(Position.Relative)
                property("z-index", "30")
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
                padding(12.px, 14.px)
                cursor("grab")
            }
            onMouseDown {
                dragging = true
                startX = it.clientX.toDouble()
                startY = it.clientY.toDouble()
            }
        }) {
            Input(InputType.Text, attrs = {
                value(title)
                style {
                    width(75.percent)
                    border(0.px)
                    backgroundColor(Color.transparent)
                    fontWeight("bold")
                    fontSize(17.px)
                    outline("none")
                    color(Color.white)
                    property("box-sizing", "border-box")
                }
                onInput { title = it.value }
                onBlur { persist() }
            })
            Div({ style { display(DisplayStyle.Flex); gap(12.px); alignItems(AlignItems.Center) } }) {
                Span({
                    style { cursor("pointer"); fontSize(18.px); color(Color.white) }
                    onClick { showMenu = !showMenu }
                }) { Text("⋯") }
                Img(src = "images/miniclose.svg", attrs = {
                    onClick {
                        persist()
                        onClose()
                    }
                    style {
                        width(16.px)
                        height(16.px)
                        cursor("pointer")
                        property("filter", "brightness(0) invert(1)")
                    }
                })
            }
        }

        if (!isPaint) Div({
            ref { element ->
                editorElement = element
                if (element.innerHTML != editorHtml) {
                    element.innerHTML = editorHtml
                }
                updateEditorOverflow()
                onDispose { if (editorElement == element) editorElement = null }
            }
            attr("contenteditable", "true")
            classes(NotepadStyles.editor, if (editorScrollEnabled) NotepadStyles.editorScrollable else NotepadStyles.editorLocked)
            style {
                width(100.percent)
                property("flex", "1")
                padding(14.px, 16.px)
                color(Color.white)
                fontSize(17.px)
                property("box-sizing", "border-box")
                outline("none")
                property("line-height", "1.4")
                property("white-space", "pre-wrap")
                property("overflow-wrap", "break-word")
                property("word-break", "break-word")
            }
            onKeyUp { syncEditorFromDom() }
            onBlur {
                snapshotSelection()
                persist()
            }
            onClick { ev ->
                val target = ev.nativeEvent.target as? HTMLElement ?: return@onClick
                if (toggleCheckboxToken(target)) {
                    syncEditorFromDom()
                    snapshotSelection()
                    persist()
                } else if (target.tagName.equals("IMG", ignoreCase = true) &&
                    target.getAttribute("src").orEmpty().startsWith("data:image/", ignoreCase = true)
                ) {
                    drawingImage = target
                    showDrawing = true
                } else {
                    syncEditorFromDom()
                    snapshotSelection()
                }
            }
            onMouseUp { snapshotSelection() }
            onKeyDown { ev ->
                when (ev.key) {
                    "Backspace" -> {
                        if (removeAdjacentToken(editorElement, backward = true)) {
                            ev.preventDefault()
                            syncEditorFromDom()
                            snapshotSelection()
                            persist()
                        }
                    }
                    "Delete" -> {
                        if (removeAdjacentToken(editorElement, backward = false)) {
                            ev.preventDefault()
                            syncEditorFromDom()
                            snapshotSelection()
                            persist()
                        }
                    }
                    "Enter" -> {
                        val mode = activeInsertMode ?: return@onKeyDown
                        ev.preventDefault()
                        restoreEditorSelection(editorElement, savedSelectionRange)
                        insertHtmlAtSelection(
                            editorElement,
                            when (mode) {
                                "STEP" -> "<br>${stepTokenHtml()}&nbsp;"
                                else -> "<br>${checkboxTokenHtml(false)}&nbsp;"
                            }
                        )
                        syncEditorFromDom()
                        snapshotSelection()
                    }
                }
            }
        }) {
            if (editorHtml.isBlank()) {
                Span({
                    style {
                        color(Color("rgba(255,255,255,0.55)"))
                    }
                }) { Text("Notiz") }
            }
        }

        Div({
            style {
                property("flex-shrink", "0")
                position(Position.Relative)
                property("z-index", "30")
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                property("border-top", "1px solid rgba(255,255,255,0.08)")
                backgroundColor(Color("rgba(0,0,0,0.10)"))
            }
        }) {
            if (showMenu) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.SpaceBetween)
                        alignItems(AlignItems.Center)
                        padding(10.px, 12.px, 8.px, 12.px)
                    }
                }) {
                    Div({ style { display(DisplayStyle.Flex); gap(8.px) } }) {
                        PRESET_BACKGROUNDS.forEach { c ->
                            Div({
                                style {
                                    width(18.px)
                                    height(18.px)
                                    borderRadius(50.percent)
                                    property("background", c)
                                    cursor("pointer")
                                    border(1.px, LineStyle.Solid, Color.black)
                                }
                                onClick {
                                    persist()
                                    onUpdate(note.copy(titel = title, beschreibung = editorElement?.innerHTML.orEmpty(), farbe = c))
                                    showMenu = false
                                }
                            })
                        }
                    }
                    Img(src = "images/miniclose.svg", attrs = {
                        onClick { onDelete() }
                        style {
                            width(18.px)
                            height(18.px)
                            cursor("pointer")
                            property("filter", "brightness(0) saturate(100%) invert(22%) sepia(94%) saturate(7447%) hue-rotate(356deg)")
                        }
                    })
                }
            }

            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(8.px)
                    padding(10.px, 12.px)
                    minHeight(54.px)
                    property("box-sizing", "border-box")
                }
            }) {
                if (!isPaint) Div({
                    style(footerButtonStyle(activeInsertMode == "STEP"))
                    onMouseDown {
                        it.preventDefault()
                        snapshotSelection()
                        activeInsertMode =
                            if (activeInsertMode == "STEP") null else "STEP"
                        if (activeInsertMode == "STEP") {
                            restoreEditorSelection(editorElement, savedSelectionRange)
                            insertHtmlAtSelection(editorElement, "${stepTokenHtml()}&nbsp;")
                            syncEditorFromDom()
                            snapshotSelection()
                            editorElement?.focus()
                        }
                    }
                }) {
                    Img(src = "images/step.svg", attrs = {
                        style {
                            width(18.px)
                            height(18.px)
                            property("filter", "brightness(0) invert(1)")
                        }
                    })
                }
                if (!isPaint) Div({
                    style(footerButtonStyle(activeInsertMode == "CHECKBOX"))
                    onMouseDown {
                        it.preventDefault()
                        snapshotSelection()
                        activeInsertMode =
                            if (activeInsertMode == "CHECKBOX") null else "CHECKBOX"
                        if (activeInsertMode == "CHECKBOX") {
                            restoreEditorSelection(editorElement, savedSelectionRange)
                            insertHtmlAtSelection(editorElement, "${checkboxTokenHtml(false)}&nbsp;")
                            syncEditorFromDom()
                            snapshotSelection()
                            editorElement?.focus()
                        }
                    }
                }) {
                    LumaticaCheckboxSymbol(checked = activeInsertMode == "CHECKBOX", fontSizePx = 18)
                }
                Div({
                    style(footerButtonStyle(showColorPalette))
                    attr("title", "Farbe")
                    onMouseDown { it.preventDefault(); showColorPalette = !showColorPalette }
                }) { Img(src = "images/farbpalette.svg", attrs = { style { width(20.px); height(20.px) } }) }
            }
            if (showColorPalette) Div({ style { padding(0.px, 12.px, 10.px) } }) {
                NoteColorPicker(noteColor) { selected ->
                        noteColor = selected
                        showColorPalette = false
                        if (!isPaint) {
                            restoreEditorSelection(editorElement, savedSelectionRange)
                            editorElement?.focus()
                            document.asDynamic().execCommand("foreColor", false, selected)
                            syncEditorFromDom()
                            snapshotSelection()
                            persist()
                        }
                    }
            }
        }

        Div({
            style {
                position(Position.Absolute)
                property("z-index", "50")
                right(0.px)
                bottom(0.px)
                width(18.px)
                height(18.px)
                cursor("nwse-resize")
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.Center)
                alignItems(AlignItems.Center)
                color(Color("rgba(255,255,255,0.55)"))
                fontSize(13.px)
                fontWeight("bold")
                property("user-select", "none")
            }
            onMouseDown {
                it.stopPropagation()
                resizing = true
                startX = it.clientX.toDouble()
                startY = it.clientY.toDouble()
                startWidth = width
                startHeight = height
            }
        }) { Text("::") }
        if (isPaint) {
        NoteDrawingOverlay(
            canvasId = "desktop-note-drawing-${note.id}",
            initialImageDataUrl = drawingData,
            initialColor = noteColor,
            showControls = false,
            interactionMode = "DRAW",
            expandableCanvas = false,
            topInsetPx = 48,
            bottomInsetPx = if (showColorPalette) 96 else 56,
            onColorChange = { noteColor = it },
            onCancel = {},
            onInsert = { dataUrl -> drawingData = dataUrl; persist() }
        )
        }
    }
}
